import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from "material-react-table";
import AxiosInstance from "../../components/Axios";
import { columnFiltersParamWithDateRange, sortingParam } from "../../common/Services";
import {
  mapJsonToRisorsaCalDav,
  mapJsonToRisorsaCalDavApiResponse,
  mapRisorsaCalDavToJson,
  MiaRisorsaCalDav,
  RisorsaCalDav,
  RisorsaCalDavApiResponse,
} from "./types";

export function useGetRisorsaCalDav(
  pagination: MRT_PaginationState,
  columnFilters: MRT_ColumnFiltersState,
  globalFilter: string,
  sorting: MRT_SortingState
) {
  return useQuery<RisorsaCalDavApiResponse>({
    queryKey: [
      "fetchRisorsaCalDav",
      columnFilters,
      globalFilter,
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
    ],
    queryFn: async () => {
      const limit = pagination.pageSize;
      const offset = pagination.pageIndex === 0 ? "0" : `${pagination.pageSize * pagination.pageIndex}`;
      const url = `risorsacaldav/?limit=${limit}&offset=${offset}&search=${globalFilter ?? ""}${columnFiltersParamWithDateRange(
        columnFilters,
        "BT90"
      )}&ordering=${sortingParam(sorting, "BT90")}`;
      const response = await AxiosInstance.get(url);
      return mapJsonToRisorsaCalDavApiResponse(response.data);
    },
    refetchOnWindowFocus: false,
  });
}

export function useGetRisorsaCalDavById(id: number | string) {
  return useQuery<RisorsaCalDav>({
    queryKey: ["fetchRisorsaCalDavById", id],
    queryFn: async () => {
      const response = await AxiosInstance.get(`risorsacaldav/${id}/`);
      return mapJsonToRisorsaCalDav(response.data.data.RisorsaCalDav);
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
}

export function useCreateRisorsaCalDav() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (item: RisorsaCalDav) => {
      const response = await AxiosInstance.post("risorsacaldav/0/", mapRisorsaCalDavToJson(item));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fetchRisorsaCalDav"] });
    },
  });
}

export function useUpdateRisorsaCalDav() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (item: RisorsaCalDav) => {
      const response = await AxiosInstance.patch(`risorsacaldav/${item.id}/`, mapRisorsaCalDavToJson(item));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fetchRisorsaCalDav"] });
      queryClient.invalidateQueries({ queryKey: ["fetchRisorsaCalDavById"] });
    },
  });
}

export function useDeleteRisorsaCalDav() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await AxiosInstance.delete(`risorsacaldav/${id}/`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fetchRisorsaCalDav"] });
    },
  });
}

export function useTestRisorsaCalDav() {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await AxiosInstance.post(`risorsacaldav/test/${id}/`);
      return response.data;
    },
  });
}

export function useSyncRisorsaCalDav() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await AxiosInstance.post(`risorsacaldav/sync/${id}/`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fetchRisorsaCalDav"] });
    },
  });
}

export function useGetMiaRisorsaCalDav() {
  return useQuery<MiaRisorsaCalDav>({
    queryKey: ["fetchMiaRisorsaCalDav"],
    queryFn: async () => {
      const response = await AxiosInstance.get("risorsacaldav/mia/");
      return response.data.data;
    },
    refetchOnWindowFocus: false,
  });
}
